import React, { useEffect } from 'react';
import DocsLayout from './DocsLayout';
import { Book, Zap, Settings, HelpCircle, Code, ArrowRight, Search } from 'lucide-react';

const DocsHome = () => {
  useEffect(() => {
    document.title = 'Documentation | TicketDesk';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Complete documentation for TicketDesk customer support software. Setup guides, integration tutorials, feature documentation, and troubleshooting help.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://www.ticketdesk.app/docs';
  }, []);

  const quickLinks = [
    {
      title: 'Getting Started',
      description: 'New to TicketDesk? Start here to set up your account and create your first ticket.',
      icon: Book,
      color: 'blue',
      links: [
        { name: 'Quick Start Guide', href: '/docs/quick-start' },
        { name: 'Account Setup', href: '/docs/account-setup' },
        { name: 'Your First Ticket', href: '/docs/first-ticket' },
      ]
    },
    {
      title: 'Integrations',
      description: 'Connect Gmail, Facebook, WhatsApp, and review monitoring platforms to TicketDesk.',
      icon: Zap,
      color: 'purple',
      links: [
        { name: 'Gmail Setup', href: '/docs/gmail-setup' },
        { name: 'Facebook Messenger', href: '/docs/facebook-setup' },
        { name: 'WhatsApp Business', href: '/docs/whatsapp-setup' },
        { name: 'Review Monitoring', href: '/docs/review-monitoring' },
      ]
    },
    {
      title: 'Features',
      description: 'Learn how to use tickets, teams, AI responses, and advanced features.',
      icon: Settings,
      color: 'green',
      links: [
        { name: 'Ticket Management', href: '/docs/ticket-management' },
        { name: 'Team Collaboration', href: '/docs/team-collaboration' },
        { name: 'AI Response Polish', href: '/docs/ai-responses' },
        { name: 'Workspaces', href: '/docs/workspaces' },
      ]
    },
    {
      title: 'Help & Support',
      description: 'Troubleshooting guides, common questions, and how to get help.',
      icon: HelpCircle,
      color: 'orange',
      links: [
        { name: 'Common Issues', href: '/docs/troubleshooting' },
        { name: 'Billing FAQ', href: '/docs/billing-faq' },
        { name: 'Contact Support', href: 'mailto:hello@ticketdesk.app' },
      ]
    },
  ];

  const popularGuides = [
    { title: 'Quick Start: Setup in 60 Seconds', href: '/docs/quick-start', tag: 'Popular' },
    { title: 'How to Connect Gmail', href: '/docs/gmail-setup', tag: 'Essential' },
    { title: 'Setting Up WhatsApp Business API', href: '/docs/whatsapp-setup', tag: 'Advanced' },
    { title: 'Understanding Review Monitoring', href: '/docs/review-monitoring', tag: 'Popular' },
    { title: 'Using AI to Polish Responses', href: '/docs/ai-responses', tag: 'New' },
    { title: 'Common Troubleshooting Issues', href: '/docs/troubleshooting', tag: 'Help' },
  ];

  return (
    <DocsLayout currentPage="/docs">
      {/* Hero Section */}
      <div className="mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          TicketDesk Documentation
        </h1>
        <p className="text-xl text-gray-600 mb-8">
          Everything you need to know about setting up and using TicketDesk for customer support.
        </p>

        {/* Search */}
        <div className="relative max-w-2xl">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search for setup guides, features, troubleshooting..."
            className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Quick Links Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {quickLinks.map((section) => {
          const Icon = section.icon;
          const colorClasses = {
            blue: 'bg-blue-50 border-blue-200 text-blue-700',
            purple: 'bg-purple-50 border-purple-200 text-purple-700',
            green: 'bg-green-50 border-green-200 text-green-700',
            orange: 'bg-orange-50 border-orange-200 text-orange-700',
          };

          return (
            <div key={section.title} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className={`inline-flex p-2 rounded-lg ${colorClasses[section.color]} mb-4`}>
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {section.title}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {section.description}
              </p>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-sm text-purple-600 hover:text-purple-700 font-medium inline-flex items-center"
                    >
                      {link.name}
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Popular Guides */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Popular Guides</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {popularGuides.map((guide) => (
            <a
              key={guide.title}
              href={guide.href}
              className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-sm transition-all group"
            >
              <div className="flex items-center flex-1">
                <Book className="w-5 h-5 text-gray-400 mr-3 group-hover:text-purple-600 transition-colors" />
                <span className="text-sm font-medium text-gray-900 group-hover:text-purple-700">
                  {guide.title}
                </span>
              </div>
              <span className={`
                text-xs px-2 py-1 rounded-full font-medium
                ${guide.tag === 'Popular' ? 'bg-blue-100 text-blue-700' : ''}
                ${guide.tag === 'Essential' ? 'bg-green-100 text-green-700' : ''}
                ${guide.tag === 'Advanced' ? 'bg-orange-100 text-orange-700' : ''}
                ${guide.tag === 'New' ? 'bg-purple-100 text-purple-700' : ''}
                ${guide.tag === 'Help' ? 'bg-red-100 text-red-700' : ''}
              `}>
                {guide.tag}
              </span>
            </a>
          ))}
        </div>
      </div>

      {/* API & Developers */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <div className="inline-flex p-2 rounded-lg bg-gray-200">
              <Code className="w-6 h-6 text-gray-700" />
            </div>
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              API Reference
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Build custom integrations and automate workflows with the TicketDesk API.
            </p>
            <a
              href="/docs/api-auth"
              className="text-sm text-purple-600 hover:text-purple-700 font-medium inline-flex items-center"
            >
              View API Documentation
              <ArrowRight className="w-3 h-3 ml-1" />
            </a>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-12 text-center p-8 bg-blue-50 rounded-lg border border-blue-100">
        <HelpCircle className="w-12 h-12 text-blue-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Can't find what you're looking for?
        </h3>
        <p className="text-gray-600 mb-4">
          Our support team is here to help you get the most out of TicketDesk.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="mailto:hello@ticketdesk.app"
            className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700"
          >
            Contact Support
          </a>
          <a
            href="https://calendly.com/hello-ticketdesk/demo"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center border border-blue-600 text-blue-600 px-6 py-2 rounded-lg font-medium hover:bg-blue-50"
          >
            Book a Demo
          </a>
        </div>
      </div>
    </DocsLayout>
  );
};

export default DocsHome;
